/**
 * Cliente de la API.
 *
 * Una clase pequeña, sin librería de datos y sin caché propia: la caché de
 * verdad de esta aplicación es IndexedDB, y la maneja la app del personal.
 * Aquí solo se habla HTTP y se traducen los errores a `ErrorApi`.
 *
 * Dos cosas que importan y no son evidentes:
 *
 *   · **El token se lee en cada petición, no se copia al construir.** La
 *     sesión caduca a las 12 horas y se renueva con el PIN sin recargar la
 *     app; si el cliente guardara una copia, seguiría mandando el viejo.
 *   · **Los trozos van en crudo, sin `FormData`.** Un multipart de 5 MB
 *     obliga al servidor a parsear y copiar el cuerpo entero; en crudo, PHP
 *     lee `php://input` en bloques de 256 kB y lo escribe directo al fichero.
 *     En un Cortex-A53 con 2 GB esa diferencia es la que hace viable subir
 *     180 MB por limpieza.
 */

import type {
    Acceso,
    Alta,
    DiaDeTrabajo,
    EstadoSubida,
    Invitacion,
    Limpieza,
    Plataforma,
    Posicion,
    RespuestaInicioSubida,
    ResolucionQr,
    SubidaConfirmada,
    Calidad,
    Gravedad,
} from '../domain/tipos.js';
import type { Idioma } from '../i18n/index.js';
import { Http } from './http.js';

/** El GPS viaja en campos sueltos, como lo espera la API. */
function aplanarGps(gps: Posicion | null | undefined): Record<string, number | string | null> {
    if (gps === null || gps === undefined) {
        return {};
    }

    return { gps_lat: gps.lat, gps_lng: gps.lng, gps_acc: gps.acc, gps_at: gps.at };
}


/**
 * Cliente de la app del personal.
 *
 * Solo escribe, y solo lo suyo: no hay aquí un método para listar limpiezas de
 * otras ni para descargar evidencia. Eso vive en `ClienteOficina`, y la
 * separación es la misma que hace el servidor con la audiencia del JWT.
 */
export class Cliente extends Http {
    // --- Alta y acceso --------------------------------------------------

    /**
     * Qué pide esta invitación, sin gastarla.
     *
     * Se pregunta antes de pintar la pantalla 00: si es **abierta** hay que
     * pedirle a la persona su nombre y sus datos; si es **dirigida** —un
     * segundo móvil, un reemplazo— oficina ya puso el nombre y solo falta el
     * PIN. Sin esto habría que elegir entre pedirle el nombre a quien ya lo
     * tiene puesto o enseñarle un formulario con el nombre de otra persona.
     */
    mirarInvitacion(token: string): Promise<Invitacion> {
        return this.pedir<Invitacion>({
            metodo: 'GET',
            ruta: `/auth/invite/${encodeURIComponent(token)}`,
        });
    }

    canjearInvitacion(datos: {
        invite_token: string;
        pin: string;
        /** Obligatorio si la invitación es abierta: lo escribe la persona. */
        nombre?: string;
        telefono?: string;
        email?: string;
        idioma?: Idioma;
        etiqueta?: string;
        modelo?: string;
        navegador?: string;
        plataforma?: Plataforma;
        app_version?: string;
    }): Promise<Alta> {
        return this.pedir<Alta>({ metodo: 'POST', ruta: '/auth/invite/redeem', cuerpo: datos });
    }

    /**
     * Poner el PIN cuando oficina lo ha restablecido.
     *
     * No es «cambiar el PIN»: el servidor solo lo acepta si no hay ninguno, y
     * el único que deja el PIN a nulo es alguien de oficina desde el panel.
     * Con el mismo móvil, sin invitación nueva y sin reinstalar nada.
     */
    crearPin(deviceToken: string, pin: string): Promise<Acceso> {
        return this.pedir<Acceso>({
            metodo: 'POST',
            ruta: '/auth/pin',
            cuerpo: { device_token: deviceToken, pin },
        });
    }

    acceder(deviceToken: string, pin: string): Promise<Acceso> {
        return this.pedir<Acceso>({
            metodo: 'POST',
            ruta: '/auth/login',
            cuerpo: { device_token: deviceToken, pin },
        });
    }

    // --- El día ---------------------------------------------------------

    dia(): Promise<DiaDeTrabajo> {
        return this.pedir<DiaDeTrabajo>({ metodo: 'GET', ruta: '/assignments/today' });
    }

    resolverQr(qrToken: string): Promise<ResolucionQr> {
        return this.pedir<ResolucionQr>({
            metodo: 'GET',
            ruta: `/qr/${encodeURIComponent(qrToken)}`,
        });
    }

    // --- Limpiezas ------------------------------------------------------

    /**
     * La API recibe el GPS en campos sueltos (`gps_lat`, `gps_lng`, `gps_acc`,
     * `gps_at`) y aquí se acepta el objeto `Posicion`, que es lo que devuelve
     * la geolocalización del navegador. La conversión vive en este sitio y no
     * en cada pantalla: es justo el tipo de desajuste que compila, pasa la
     * revisión y falla en el móvil.
     */
    crearLimpieza(datos: {
        id: string;
        property_id?: string;
        qr_token?: string;
        assignment_id?: string | null;
        started_at: string;
        modo_captura?: 'video' | 'fotos';
        app_version?: string;
        gps?: Posicion | null;
        estancias: { id: string; room_id: string }[];
    }): Promise<Limpieza> {
        const { gps, ...resto } = datos;

        return this.pedir<Limpieza>({
            metodo: 'POST',
            ruta: '/cleanings',
            cuerpo: { ...resto, ...aplanarGps(gps) },
        });
    }

    /** Cierra la limpieza. `status: 'completa'` es lo que dispara el cierre. */
    finalizarLimpieza(
        id: string,
        datos: {
            ended_at: string;
            notas?: string | null;
            gps?: Posicion | null;
            estancias: {
                id: string;
                estado: 'pendiente' | 'hecha' | 'no_accesible';
                motivo_no_accesible?: string | null;
                video_ms?: number | null;
            }[];
        }
    ): Promise<Limpieza> {
        const { gps, ...resto } = datos;

        return this.pedir<Limpieza>({
            metodo: 'PATCH',
            ruta: `/cleanings/${id}`,
            cuerpo: { ...resto, ...aplanarGps(gps), status: 'completa' },
        });
    }

    actualizarLimpieza(id: string, datos: Record<string, unknown>): Promise<Limpieza> {
        return this.pedir<Limpieza>({
            metodo: 'PATCH',
            ruta: `/cleanings/${id}`,
            cuerpo: datos,
        });
    }

    limpieza(id: string): Promise<Limpieza> {
        return this.pedir<Limpieza>({ metodo: 'GET', ruta: `/cleanings/${id}` });
    }

    // --- Subida troceada ------------------------------------------------

    iniciarSubida(datos: {
        asset_id: string;
        cleaning_id: string;
        cleaning_room_id?: string | null;
        room_punto_id?: string | null;
        tipo: 'video' | 'foto' | 'incidencia_foto';
        mime: string;
        bytes: number;
        sha256: string;
        capturado_at: string;
        duracion_ms?: number | null;
        ancho?: number | null;
        alto?: number | null;
        calidad?: Calidad | null;
        gps_lat?: number | null;
        gps_lng?: number | null;
        gps_acc?: number | null;
    }): Promise<RespuestaInicioSubida> {
        return this.pedir<RespuestaInicioSubida>({
            metodo: 'POST',
            ruta: '/uploads/init',
            cuerpo: datos,
        });
    }

    /**
     * Un trozo, en crudo. PUT y no POST porque mandarlo dos veces tiene que
     * dar el mismo resultado, y eso es exactamente lo que significa PUT.
     */
    subirTrozo(
        uploadId: string,
        n: number,
        trozo: Blob,
        señal?: AbortSignal
    ): Promise<EstadoSubida> {
        return this.pedir<EstadoSubida>({
            metodo: 'PUT',
            ruta: `/uploads/${uploadId}/chunk/${n}`,
            binario: trozo,
            ...(señal !== undefined ? { señal } : {}),
        });
    }

    completarSubida(uploadId: string, sha256: string): Promise<SubidaConfirmada> {
        return this.pedir<SubidaConfirmada>({
            metodo: 'POST',
            ruta: `/uploads/${uploadId}/complete`,
            cuerpo: { sha256 },
        });
    }

    estadoSubida(uploadId: string): Promise<EstadoSubida> {
        return this.pedir<EstadoSubida>({ metodo: 'GET', ruta: `/uploads/${uploadId}` });
    }

    // --- Incidencias ----------------------------------------------------

    crearIncidencia(datos: {
        id: string;
        cleaning_id: string;
        cleaning_room_id?: string | null;
        texto: string;
        gravedad?: Gravedad;
    }): Promise<{ creada: boolean; incidencia: { id: string } }> {
        return this.pedir({ metodo: 'POST', ruta: '/incidents', cuerpo: datos });
    }

    salud(): Promise<Record<string, unknown>> {
        return this.pedir({ metodo: 'GET', ruta: '/health' });
    }
}
